import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/en-ca";
import { useI18n } from "../i18n/LanguageProvider";
import "../styles/components/programmation-calendar.scss";

function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function getEventTitle(ev, lang) {
  if (ev && ev.title && typeof ev.title === "object") {
    if (lang === "en") return ev.title.en || ev.title.fr || ev.eventName || "";
    return ev.title.fr || ev.title.en || ev.eventName || "";
  }

  const en = [ev?.eventNameEn, ev?.titleEn];
  const fr = [ev?.eventNameFr, ev?.titleFr];
  const generic = [ev?.eventName, ev?.title];

  if (lang === "en") return en.find(Boolean) || generic.find(Boolean) || "";
  return fr.find(Boolean) || generic.find(Boolean) || "";
}

function getEventLocation(ev, lang) {
  if (lang === "en") return ev?.locationEn ?? ev?.location ?? "";
  return ev?.locationFr ?? ev?.location ?? "";
}

function getEventDescription(ev, lang) {
  if (!ev) return "";

  if (ev.description && typeof ev.description === "object") {
    if (lang === "en") return ev.description.en || ev.description.fr || "";
    return ev.description.fr || ev.description.en || "";
  }

  if (lang === "en") return ev.descriptionEn || ev.description || "";
  return ev.descriptionFr || ev.description || "";
}

function parseTimeToDecimal(timeStr) {
  if (!timeStr || typeof timeStr !== "string") return null;

  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours + minutes / 60;
}

function getEventStart(ev) {
  return ev?.start || ev?.startTime || ev?.timeStart || null;
}

function getEventEnd(ev) {
  return ev?.end || ev?.endTime || ev?.timeEnd || null;
}

function getEventDayKey(ev) {
  const raw = ev?.day || ev?.weekday || ev?.jour || ev?.dayKey || "";
  const s = slugify(raw);

  if (["friday", "vendredi"].includes(s)) return "friday";
  if (["saturday", "samedi"].includes(s)) return "saturday";
  if (["sunday", "dimanche"].includes(s)) return "sunday";

  if (ev?.date) {
    const m = moment(
      ev.date,
      [moment.ISO_8601, "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss"],
      true
    );

    if (m.isValid()) {
      const day = m.day();
      if (day === 5) return "friday";
      if (day === 6) return "saturday";
      if (day === 0) return "sunday";
    }
  }

  return null;
}

const GRID_DAY_KEYS = ["saturday", "sunday"];
const HOUR_HEIGHT = 88;
const START_HOUR = 10;
const END_HOUR = 22;

const CalendrierProg = ({ events = [] }) => {
  const { lang } = useI18n();
  const [selectedEvent, setSelectedEvent] = useState(null);

  moment.locale(lang === "fr" ? "fr" : "en-ca");

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelectedEvent(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    if (selectedEvent) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [selectedEvent]);

  const dayLabels = useMemo(
    () => ({
      friday: lang === "en" ? "Friday, April 3" : "Vendredi 3 avril",
      saturday: lang === "en" ? "Saturday, April 4" : "Samedi 4 avril",
      sunday: lang === "en" ? "Sunday, April 5" : "Dimanche 5 avril",
    }),
    [lang]
  );

  const uiText = useMemo(
    () => ({
      time: lang === "en" ? "Time" : "Heure",
      details: lang === "en" ? "View details" : "Voir les détails",
      helper:
        lang === "en"
          ? "Click on an event to read the full description."
          : "Cliquez sur un événement pour lire la description complète.",
      noDescription:
        lang === "en"
          ? "No description available."
          : "Aucune description disponible.",
    }),
    [lang]
  );

  const hours = useMemo(() => {
    const list = [];
    for (let h = START_HOUR; h < END_HOUR; h += 1) {
      list.push(`${String(h).padStart(2, "0")}:00`);
    }
    return list;
  }, []);

  const normalizedEvents = useMemo(() => {
    const safeEvents = Array.isArray(events) ? events : [];

    return safeEvents
      .map((ev) => {
        const dayKey = getEventDayKey(ev);
        const startRaw = getEventStart(ev);
        const endRaw = getEventEnd(ev);

        const startDecimal = parseTimeToDecimal(startRaw);
        const endDecimal = parseTimeToDecimal(endRaw);

        if (
          !dayKey ||
          startDecimal === null ||
          endDecimal === null ||
          endDecimal <= startDecimal
        ) {
          return null;
        }

        return {
          ...ev,
          dayKey,
          startRaw,
          endRaw,
          startDecimal,
          endDecimal,
          typeClass: `type-${slugify(ev.type || ev.calendar || "")}`,
        };
      })
      .filter(Boolean);
  }, [events]);

  const eventsByDay = useMemo(
    () => ({
      friday: normalizedEvents.filter((ev) => ev.dayKey === "friday"),
      saturday: normalizedEvents.filter((ev) => ev.dayKey === "saturday"),
      sunday: normalizedEvents.filter((ev) => ev.dayKey === "sunday"),
    }),
    [normalizedEvents]
  );

  const fridayEvents = eventsByDay.friday;

  const getEventStyle = (ev) => {
    const top = (ev.startDecimal - START_HOUR) * HOUR_HEIGHT;
    const height = (ev.endDecimal - ev.startDecimal) * HOUR_HEIGHT;

    return {
      top: `${top}px`,
      height: `${Math.max(height, 72)}px`,
    };
  };

  const totalTrackHeight = (END_HOUR - START_HOUR) * HOUR_HEIGHT;

  return (
    <div className="schedule-wrapper">
      {fridayEvents.length > 0 && (
        <section className="friday-feature">
          <div className="friday-feature-head">
            <div className="friday-feature-label">{dayLabels.friday}</div>
          </div>

          <div className="friday-feature-list">
            {fridayEvents.map((ev) => {
              const title = getEventTitle(ev, lang);
              const location = getEventLocation(ev, lang);

              return (
                <button
                  key={ev.id || `friday-${title}-${ev.startRaw}`}
                  type="button"
                  className={`event-card friday-feature-card ${ev.typeClass}`}
                  onClick={() => setSelectedEvent(ev)}
                >
                  <div className="event-title">{title}</div>
                  <div className="event-meta">
                    {ev.startRaw} — {ev.endRaw}
                    {location ? ` • ${location}` : ""}
                  </div>
                  <div className="event-cta">
                    {uiText.details}
                    <span className="event-cta-arrow">↗</span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <div className="schedule">
        <div
          className="schedule-grid"
          style={{ gridTemplateColumns: "92px repeat(2, minmax(0, 1fr))" }}
        >
          <div className="time-header">{uiText.time}</div>

          {GRID_DAY_KEYS.map((dayKey) => (
            <div key={dayKey} className="day-header">
              {dayLabels[dayKey]}
            </div>
          ))}

          <div className="time-col" style={{ height: `${totalTrackHeight}px` }}>
            {hours.map((hour) => (
              <div key={hour} className="time-slot">
                {hour}
              </div>
            ))}
          </div>

          {GRID_DAY_KEYS.map((dayKey) => (
            <div key={dayKey} className="day-col">
              <div
                className="day-track"
                style={{ height: `${totalTrackHeight}px` }}
              >
                {eventsByDay[dayKey].map((ev) => {
                  const title = getEventTitle(ev, lang);
                  const location = getEventLocation(ev, lang);

                  return (
                    <button
                      key={ev.id || `${dayKey}-${title}-${ev.startRaw}`}
                      type="button"
                      className={`event-card ${ev.typeClass}`}
                      style={getEventStyle(ev)}
                      onClick={() => setSelectedEvent(ev)}
                    >
                      <div className="event-title">{title}</div>
                      <div className="event-meta">
                        {ev.startRaw} — {ev.endRaw}
                        {location ? ` • ${location}` : ""}
                      </div>
                      <div className="event-cta">
                        {uiText.details}
                        <span className="event-cta-arrow">↗</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedEvent && (
        <div
          className="event-modal-overlay"
          onClick={() => setSelectedEvent(null)}
        >
          <div
            className={`event-modal type-${slugify(
              selectedEvent.type || selectedEvent.calendar || ""
            )}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="event-modal-title"
          >
            <button
              type="button"
              className="event-modal-close"
              onClick={() => setSelectedEvent(null)}
              aria-label={lang === "en" ? "Close" : "Fermer"}
            >
              ×
            </button>

            <h2 className="event-modal-title" id="event-modal-title">
              {getEventTitle(selectedEvent, lang)}
            </h2>

            <div className="event-modal-meta">
              {selectedEvent.startRaw} — {selectedEvent.endRaw}
              {getEventLocation(selectedEvent, lang)
                ? ` • ${getEventLocation(selectedEvent, lang)}`
                : ""}
            </div>

            <div className="event-modal-desc">
              {getEventDescription(selectedEvent, lang) || uiText.noDescription}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendrierProg;